import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signUpWithEmail, signInWithGoogle, createUserDoc, getUserDoc } from '../../services/firebase';

const SignUp = ({ role: presetRole }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [step, setStep] = useState(presetRole ? 'form' : 'role');
  const [role, setRole] = useState(presetRole || null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    // Students without an invite token hit a dead end — show info screen instead
    if (selectedRole === 'student' && !inviteToken) {
      setStep('student-info');
    } else {
      setStep('form');
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const result = await signUpWithEmail(email, password);
      await createUserDoc(result.user.uid, {
        role,
        email,
        displayName: name,
        ageConfirmed: role === 'parent', // parents skip age gate
      });
      // Routing handled by App.js based on role + familyId
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    if (!role) return;
    setError('');
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      const existing = await getUserDoc(result.user.uid);
      if (!existing) {
        await createUserDoc(result.user.uid, {
          role,
          email: result.user.email,
          displayName: result.user.displayName || '',
          ageConfirmed: role === 'parent',
        });
      }
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Role selection
  if (step === 'role') {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={logoStyle}>W</div>
            <h1 style={{ fontSize: '1.75rem', marginTop: 16 }}>Create your account</h1>
            <p style={{ color: 'var(--color-text-2)', marginTop: 6 }}>
              Who are you setting this up for?
            </p>
          </div>

          <div className="stack stack-4">
            <button className="card" style={roleCardStyle} onClick={() => handleRoleSelect('parent')}>
              <div style={roleIconStyle}>👨‍👩‍👧</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>I'm a parent</div>
                <div style={{ color: 'var(--color-text-2)', fontSize: '0.875rem', marginTop: 4 }}>
                  Set up and manage my student's allowance
                </div>
              </div>
              <span style={{ marginLeft: 'auto', color: 'var(--color-text-3)' }}>→</span>
            </button>

            <button className="card" style={roleCardStyle} onClick={() => handleRoleSelect('student')}>
              <div style={roleIconStyle}>🎓</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>I'm a student</div>
                <div style={{ color: 'var(--color-text-2)', fontSize: '0.875rem', marginTop: 4 }}>
                  {inviteToken ? 'Accept my parent\'s invite' : 'Track my spending and savings'}
                </div>
              </div>
              <span style={{ marginLeft: 'auto', color: 'var(--color-text-3)' }}>→</span>
            </button>
          </div>

          <p style={{ textAlign: 'center', marginTop: 32, fontSize: '0.9rem', color: 'var(--color-text-2)' }}>
            Already have an account?{' '}
            <Link to="/signin" style={{ fontWeight: 600 }}>Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  // Step 1b: Student without invite — explain how it works
  if (step === 'student-info') {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div style={{ width: '100%', maxWidth: 400 }}>
          <button onClick={() => setStep('role')}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-text-2)', marginBottom: 24, fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' }}>
            ← Back
          </button>

          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎓</div>
            <h1 style={{ fontSize: '1.75rem', marginBottom: 12 }}>Students join by invite</h1>
            <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6 }}>
              Campus Wallet is set up by parents. Your parent needs to create an account first
              and they'll send you a personal invite link to connect your wallet.
            </p>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <div className="stack stack-3">
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>1️⃣</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Parent signs up</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', marginTop: 2 }}>
                    They set your allowance and savings match
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>2️⃣</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>You get an invite link</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', marginTop: 2 }}>
                    Check your email for a link from Campus Wallet
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>3️⃣</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Tap the link to connect</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', marginTop: 2 }}>
                    Your wallet is ready to go
                  </div>
                </div>
              </div>
            </div>
          </div>

          <a href={`mailto:campusbudgetapp@gmail.com?subject=My student wants to join Campus Wallet`}
            className="btn btn--secondary btn--full"
            style={{ textAlign: 'center', marginBottom: 12, display: 'block' }}>
            Contact us for help
          </a>

          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--color-text-2)' }}>
            Already have an invite?{' '}
            <Link to="/signin" style={{ fontWeight: 600 }}>Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Account form
  return (
    <div className="screen screen--centered screen--no-nav">
      <div style={{ width: '100%', maxWidth: 400 }}>
        <button
          onClick={() => setStep('role')}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-2)', marginBottom: 24, fontSize: '0.9rem',
            display: 'flex', alignItems: 'center', gap: 6 }}
        >
          ← Back
        </button>

        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: '1.75rem' }}>
            {role === 'parent' ? 'Parent account' : 'Student account'}
          </h1>
          <p style={{ color: 'var(--color-text-2)', marginTop: 6 }}>
            {role === 'parent'
              ? 'You\'ll set up your student\'s wallet after signing up.'
              : 'You\'ll link to your parent\'s account after signing up.'}
          </p>
        </div>

        {error && <div className="alert alert--error" style={{ marginBottom: 20 }}>{error}</div>}

        <form onSubmit={handleSignUp} className="stack stack-4">
          <div className="form-group">
            <label className="form-label">Your name</label>
            <input
              className="form-input"
              type="text"
              placeholder={role === 'parent' ? 'Jane Smith' : 'Alex Smith'}
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              className="form-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
                style={{ paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  color: 'var(--color-text-3)', fontSize: '0.875rem', fontFamily: 'var(--font-body)' }}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn--primary btn--full btn--lg"
            disabled={loading}
            style={{ marginTop: 8 }}
          >
            {loading
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : 'Create Account'}
          </button>
        </form>

        <div className="divider" style={{ margin: '24px 0' }}>or</div>

        <button
          className="btn btn--google btn--full"
          onClick={handleGoogleSignUp}
          disabled={loading}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.31z"/>
          </svg>
          Continue with Google
        </button>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.8125rem',
          color: 'var(--color-text-3)', lineHeight: 1.5 }}>
          By signing up you agree to our{' '}
          <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

const logoStyle = {
  width: 64, height: 64, borderRadius: 16, background: '#2D6A4F',
  color: 'white', display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', fontSize: 32, fontFamily: 'Georgia, serif', fontWeight: 700,
};

const roleCardStyle = {
  display: 'flex', alignItems: 'center', gap: 16,
  cursor: 'pointer', textAlign: 'left', width: '100%',
  background: 'none', fontFamily: 'inherit',
};

const roleIconStyle = {
  width: 48, height: 48, borderRadius: 12, background: 'var(--color-surface-2)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 24, flexShrink: 0,
};

const friendlyError = (code) => {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

export default SignUp;
